/**
 * PR-A2 (ADO 286302): the reserved-height QA answer slot.
 *
 * graft/A6: extracted verbatim from AiSearch.tsx so the routed full-page
 * surface can reuse the layout contract rather than reimplement it. Nothing
 * here knows whether it is rendering inside a dropdown or a page — the only
 * surface-specific value is the reserved height, which is why
 * `QA_SLOT_HEIGHT_PX` is exported (the page re-derives it for its own width).
 */
import { useEffect, useState } from "react";

import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";

import { AiSearchQA } from "./AiSearchQA";
import type { AiSearchPhase, IQAAnswer } from "./types";

/**
 * PR-A2 (ADO 286302): fixed pixel height of the reserved QA answer slot. The
 * slot renders at this height the moment intent reports isQuestion (skeleton
 * card) and keeps it when the qa event replaces the skeleton with the answer
 * — so the arrival of the answer can never change the y-position of the
 * result list below it. Inline style, not a class, so the layout contract is
 * assertable in the DOM-level tests.
 */
export const QA_SLOT_HEIGHT_PX = 132;

/**
 * A2-review (thread 1511891): answers longer than this line-clamp inside the
 * fixed slot and get a "Show full answer" toggle instead of clipping behind
 * an undiscoverable nested scrollbar. Expanding is a user-initiated layout
 * change (the slot grows to fit), which D4 permits — only PASSIVE arrivals
 * may never move painted content. ~3 clamp lines of answer text.
 */
export const QA_CLAMP_THRESHOLD_CHARS = 220;

/**
 * PR-A2: placeholder rendered inside the reserved QA slot between the intent
 * event (isQuestion) and the qa event. Same card chrome as the real answer so
 * the swap is a content change, not a layout change.
 */
function QASkeletonCard() {
    return (
        <div className="ai-search-qa" data-testid="ai-search-qa-skeleton">
            <div className="ai-search-qa-card">
                <div className="ai-search-qa-label">AI Answer</div>
                <div className="ai-search-skeleton-lines">
                    <div className="ai-search-skeleton-line ai-search-skeleton-line--medium" />
                    <div className="ai-search-skeleton-line ai-search-skeleton-line--short" />
                </div>
            </div>
        </div>
    );
}

interface IAiSearchQASlotProps {
    qaAnswers: Record<string, IQAAnswer>;
    phase: AiSearchPhase;
    isQuestion: boolean;
    hasQuery: boolean;
    /** Resets the expansion toggle when a new search starts. */
    searchId: string;
    /** Reserved height in pixels. Surfaces override for their own width. */
    heightPx?: number;
}

/**
 * Renders the reserved QA slot, or nothing when no slot is owed.
 *
 * The slot appears the moment intent reports isQuestion (a fixed-height
 * skeleton card) and the eventual qa event replaces the skeleton IN PLACE, so
 * the answer's arrival can never shove the result list below it.
 *
 * A2-review (thread 1511890): once reserved, the slot holds its height for the
 * REST OF THE SEARCH — collapsing at an answerless `done` would yank the list
 * UP mid-read, the same shove relocated to stream end. An answerless end swaps
 * the shimmer for a quiet "no answer" state at the SAME height. The slot leaves
 * only with the whole view: new search, clear/cancel (phase idle), or close.
 */
export function AiSearchQASlot(props: IAiSearchQASlotProps) {
    const {
        qaAnswers,
        phase,
        isQuestion,
        hasQuery,
        searchId,
        heightPx = QA_SLOT_HEIGHT_PX,
    } = props;

    // A2-review (thread 1511891): long answers line-clamp inside the fixed
    // slot with a "Show full answer" toggle instead of hiding overflow behind
    // a nested scrollbar. Expansion is user-initiated (D4 allows it); it
    // resets per search.
    const [qaExpanded, setQaExpanded] = useState(false);
    useEffect(() => {
        setQaExpanded(false);
    }, [searchId]);

    const hasQA = Object.keys(qaAnswers).length > 0;
    const qaStreamOpen = phase === "loading" || phase === "streaming";
    const showQASlot = hasQA || (isQuestion && hasQuery && phase !== "idle");

    const qaNeedsClamp = Object.values(qaAnswers).some(
        (a) => a.text.length > QA_CLAMP_THRESHOLD_CHARS,
    );
    const qaSlotExpanded = qaExpanded && qaNeedsClamp;

    if (!showQASlot) return null;

    const qaSlotContent: "answer" | "skeleton" | "no-answer" = hasQA
        ? "answer"
        : qaStreamOpen
          ? "skeleton"
          : "no-answer";

    return (
        <div
            className="ai-search-qa-slot"
            style={
                qaSlotExpanded ? { minHeight: heightPx } : { height: heightPx, overflow: "hidden" }
            }
            data-testid="ai-search-qa-slot"
        >
            {qaSlotContent === "answer" && (
                <>
                    <AiSearchQA qaAnswers={qaAnswers} clamped={qaNeedsClamp && !qaExpanded} />
                    {qaNeedsClamp && (
                        <BTButton
                            type="tertiary"
                            onClick={() => setQaExpanded((v) => !v)}
                            data-testid="ai-search-qa-expand"
                            className="ai-search-qa-expand"
                        >
                            {qaExpanded ? "Show less" : "Show full answer"}
                        </BTButton>
                    )}
                </>
            )}
            {qaSlotContent === "skeleton" && <QASkeletonCard />}
            {qaSlotContent === "no-answer" && (
                <div className="ai-search-qa" data-testid="ai-search-qa-no-answer">
                    <div className="ai-search-qa-card">
                        <div className="ai-search-qa-label">AI Answer</div>
                        <div className="ai-search-qa-text ai-search-qa-text--muted">
                            No answer available for this question &mdash; the results below may
                            help.
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
